import React, { forwardRef, useContext, useEffect } from 'react';

import {
  AppContainerContext,
  Comp,
  Flexbox,
  FocusWithinGroupContext,
  ListboxContext,
  getContainerProps,
  useTheme,
} from '@redsift/design-system';
import { SelectContentProps } from './types';
import {
  FloatingPortal,
  PopoverContent,
  StyledPopoverContent,
  useMergeRefs,
  usePopoverContext,
} from '@redsift/popovers';
import classNames from 'classnames';

const COMPONENT_NAME = 'SelectContent';
const CLASSNAME = 'redsift-select-content';

/**
 * The SelectContent component.
 */
export const SelectContent: Comp<SelectContentProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { children, className, style } = props;
  const containerProps = getContainerProps(props);

  const { getFloatingProps, isOpen, refs, strategy, x, y } = usePopoverContext();
  const popoverRef = useMergeRefs([refs.setFloating, ref]);

  const appContainerState = useContext(AppContainerContext);
  const focusContext = useContext(FocusWithinGroupContext);
  const listboxState = useContext(ListboxContext);
  const theme = useTheme();

  useEffect(() => {
    if (focusContext && focusContext.state.delayedAction && focusContext.state.tabStops.length) {
      focusContext.dispatch(focusContext.state.delayedAction);
    }
  }, [focusContext.state.delayedAction, focusContext.state.tabStops.length]);

  return (
    <FloatingPortal root={appContainerState?.appContainerRef.current}>
      {isOpen && (
        <StyledPopoverContent
          $theme={theme!}
          ref={popoverRef}
          {...getFloatingProps(props)}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            ...style,
          }}
          className={classNames(PopoverContent.className, SelectContent.className, className)}
          aria-disabled={listboxState.state.isDisabled}
        >
          <Flexbox flexDirection="column" gap="0px" margin="8px 0px" width="100%" {...containerProps}>
            {children}
          </Flexbox>
        </StyledPopoverContent>
      )}
    </FloatingPortal>
  );
});
SelectContent.className = CLASSNAME;
SelectContent.displayName = COMPONENT_NAME;
